/**
 * Connect — Konva swimlane renderer (OMON-215: continuous axis + device gutter).
 *
 * The swimlane is ONE continuous horizontal time axis over the fortnight window
 * (replacing OMON-209's per-day block stack). Lanes are grouped **by device**;
 * each group has a frozen left **gutter** — the device photo, name/location, and
 * a health badge (battery / signal / last-seen, OMON-214) — that never scrolls
 * away horizontally as you pan time. Clicking the gutter raises that device's
 * sensor filter (the per-device replacement for the global "Filter sensors"
 * button), via the `onDeviceFilter` callback.
 *
 * Continuous lanes draw run-length-encoded colour bands; state lanes draw
 * on/off bands; event lanes draw scatter marks. A band extends to the next
 * reading's time; the final (open) band stops at the data horizon — never the
 * future. Day boundaries are drawn as subtle full-height gridlines + top-ruler
 * labels; zooming in past ~2 days adds hour ticks.
 *
 * Interaction: horizontal drag / two-finger scroll pans TIME; ctrl+wheel / pinch
 * zooms time (≥30-min span); plain vertical wheel scrolls the device groups
 * (native). Hover/tap a mark → its reading + time. Redraws are rAF-coalesced.
 */
import Konva from "konva";
import { timeFormat } from "d3-time-format";
import { timeTicks } from "d3-time";
import { paletteColor } from "../sensor-types";
import type { LaneKind, PaletteName } from "../sensor-types";
import type { DeviceHealth } from "../sensor-series";
import { bandEndBeforeGap, isSilence } from "./band-gaps";

export interface RenderPoint {
  t: Date;
  value: number | null;
}

export interface RenderLane {
  deviceId: string; // groups lanes into device blocks
  deviceType: string; // e.g. "powerPlug" | "enterprise"
  deviceImage: string | null; // _deviceDefinition photo URL (gutter)
  deviceName: string; // e.g. "Smart Plug"
  topLabel: string; // device location, e.g. "Bedroom"
  subLabel: string; // channel, e.g. "Temperature (°C)"
  /** `${propertyPath}::${subtype}` — the per-user filter key (OMON-212). */
  filterKey: string;
  kind: LaneKind;
  /** OMON-295: which palette a gradient lane interpolates through. */
  palette: PaletteName;
  colorFor: (v: number | null) => string;
  valueLabel: (v: number | null) => string;
  points: RenderPoint[];
}

/** One device's lanes + identity + health, drawn as a gutter cell + its rows. */
interface DeviceGroup {
  deviceId: string;
  deviceType: string;
  deviceImage: string | null;
  deviceName: string;
  location: string;
  health: DeviceHealth | null;
  lanes: RenderLane[];
}

const LANE_H_BASE = 30;
const LANE_GAP = 4;
const GROUP_GAP = 12;
const GUTTER_W_MIN = 140; // frozen device gutter — grows to fit content
const GUTTER_W_MAX = 280; // …but never wider than this (lanes need room)
const RIGHT = 16;
// 34, not 26 (Ed 2026-08-26, third ruler iteration): the ruler holds TWO
// text tiers when zoomed in — dates over hours — and 26px forced them to
// touch. Two honest rows: dates ~3–13, clear gap, hours ~17–28.
const RULER_H_BASE = 34;
const TOP = 4;
const IMG_BASE = 48; // device photo edge in the gutter
const MIN_SPAN_MS = 30 * 60_000; // tightest zoom ≈ 30 min
const DAY_MS = 86_400_000;
const GRADIENT_NULL = "rgb(180,180,180)"; // OMON-223: null reading in a gradient lane
// OMON-292: the sticky lane-name chip. A SCRIM — opaque under the text, fading
// to nothing — rather than a solid pill, so the band colour still reads through
// and there is no hard edge that looks like a gap in the data.
const CHIP_FONT = 10;
const CHIP_H_BASE = 15;
const CHIP_PAD = 6; // gutter edge → text
const CHIP_FADE = 30; // px over which the scrim tapers to fully transparent
const CHIP_MAX_FRAC = 0.28; // a long channel name never eats more plot than this
const CHIP_SCRIM = "rgba(255,255,255,0.88)";
const fmtDay = timeFormat("%a %d %b");
const fmtHour = timeFormat("%H:%M");
const fmtStamp = timeFormat("%d %b %H:%M");
// OMON-290: month-scale labels for year-wide views ("Dec 2025").
const fmtMonth = timeFormat("%b %Y");
// OMON-294: ruler label density is a function of PIXELS ÷ MEASURED LABEL WIDTH,
// never of the time span. Fonts here must match what drawRuler renders — they
// are what the fit is measured against.
const RULER_GAP = 14; // minimum clear space between adjacent ruler labels
const HOUR_GAP = 10;
// The time row's interval ladder, in minutes. Sub-hour tiers exist because a
// deep zoom otherwise strands the reader at hour labels with a screenful of
// unlabeled space between them (Ed, 2026-08-26); 5 min is the floor.
const TIME_STEPS_MIN = [5, 10, 15, 30, 60, 120, 180, 360, 720];

/** OMON-295: the visible range for a lane chip. Precision follows the SPAN, not
 *  the magnitude — 18–24 °C wants a decimal, 0–1400 lux does not, and a latency
 *  range of 0.02–0.09 s would round to "0–0" at any fixed precision. */
function fmtRange(lo: number, hi: number): string {
  const span = Math.abs(hi - lo);
  const dp = span >= 10 ? 0 : span >= 1 ? 1 : 2;
  return `${lo.toFixed(dp)}–${hi.toFixed(dp)}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** "5m" / "2h" / "3d" ago — compact relative age for the last-seen badge. */
function ago(then: Date, now: number): string {
  const s = Math.max(0, Math.round((now - then.getTime()) / 1000));
  if (s < 90) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 90) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 36) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export class TimelineRenderer {
  /** Set by the view: raise the per-device sensor filter (OMON-215). */
  onDeviceFilter: ((deviceId: string, x: number, y: number) => void) | null = null;
  /** OMON-222: fired (debounced) when the visible time window settles after a
   *  zoom/pan, so the view can re-fetch at a resolution matched to the span. */
  onViewChange: ((start: Date, end: Date) => void) | null = null;
  private viewChangeTimer: number | null = null;

  private readonly container: HTMLElement;
  private stage: Konva.Stage | null = null;
  private layer: Konva.Layer | null = null;
  private tooltipEl: HTMLDivElement | null = null;

  // ── Layering (2026-07-08 perf review, tier 3) ─────────────────────────
  // The scene splits into four groups inside the one layer:
  //   bgGroup      — lane backgrounds (view-independent)
  //   clipGroup    — clip rect over the plot area, holding…
  //   contentGroup — day grid + bands + dots, built ONCE per settle over the
  //                  FULL loaded range, then CACHED to a bitmap. Pan/zoom are
  //                  pure transforms of this group (a blit), not rebuilds.
  //   gutterGroup  — frozen device gutter + sticky lane-name chips: everything
  //                  pinned to the viewport rather than to time. Takes no view
  //                  transform, so "sticky" is free and costs nothing per frame
  //                  (view-independent)
  //   overlayGroup — ruler labels + now marker, redrawn per frame (cheap).
  // The view clamps to the loaded range, so a translated content bitmap can
  // never expose blank canvas.
  private contentGroup: Konva.Group | null = null;
  private overlayGroup: Konva.Group | null = null;
  /** The view the content bitmap was built for (transform baseline). */
  private builtView: [number, number] = [0, 0];
  private builtPxPerMs = 0;
  /** The time span the content bitmap COVERS: view padded by one view-width
   *  each side, clamped to the loaded range. Unbounded coverage would make a
   *  deep zoom-in rasterise the whole range at fine scale (a canvas hundreds of
   *  view-widths wide); bounded, a pan can only reach blank canvas after
   *  travelling a full view-width — and the 250ms settle rebuild recenters
   *  the bitmap long before that reads as missing data. */
  private builtRange: [number, number] = [0, 0];
  /** Post-gesture settle timer: rebuild at the final scale (re-crisps dots,
   *  re-stretches gradient palettes, re-thins ruler labels). */
  private settleTimer: number | null = null;

  private groups: DeviceGroup[] = [];
  private range: [number, number] = [0, 0]; // full data window (ms)
  private view: [number, number] = [0, 0]; // visible time window (ms)
  private width = 0;
  private height = 0;
  private horizon = 0; // latest real sample (ms) — bands never paint past it
  private gutterW = GUTTER_W_MIN; // computed per-render to fit the widest tile

  private dragging = false;
  private lastPointerX = 0;
  /** Lane hit-map rebuilt each redraw: analytic hover replaces per-shape Konva
   *  hit-canvas testing (every band/dot used to be `listening` — doubling draw
   *  cost across the whole scene just to answer "what's under the cursor"). */
  private laneSlots: { lane: RenderLane; y: number }[] = [];
  /** Per-points-array colour caches — colours for state lanes depend only on
   *  the data, and gradient colours only on (data, visible lo/hi). Keyed by the
   *  points ARRAY identity: every data refresh replaces the array, so stale
   *  entries fall out of the WeakMap automatically. */
  private readonly stateColorCache = new WeakMap<RenderPoint[], string[]>();
  private readonly gradientCache = new WeakMap<
    RenderPoint[],
    { lo: number; hi: number; colors: string[] }
  >();
  /** OMON-295: visible extremes the gradient palette was stretched over, per
   *  lane — read by the chip. Lane objects are replaced on every data refresh,
   *  so stale entries fall out of the WeakMap on their own. */
  private readonly laneRange = new WeakMap<RenderLane, [number, number]>();
  /** OMON-295: the shared diagonal-hatch tile for state-0 bands. */
  private hatchCanvas: HTMLCanvasElement | null = null;

  // ── OMON-296: type scale ──────────────────────────────────────────────
  // A canvas opts OUT of the user's text-size preference unless you opt back
  // in. `fontSize: 10` is ten device-independent pixels whatever the browser or
  // OS is set to — so every other element on the page honours the setting and
  // the timeline silently ignores it. Same class as OMON-293 and OMON-294: a
  // constant that encodes an assumption about the viewing context. Everything
  // here that exists to HOLD text is derived from the root font size.
  private ts = 1;
  private laneH = LANE_H_BASE;
  private rulerH = RULER_H_BASE;
  private chipH = CHIP_H_BASE;
  private img = IMG_BASE;

  /** Scale a base px size. Floored so the 8px hour row cannot vanish. */
  private fs(base: number): number {
    return Math.max(7, Math.round(base * this.ts));
  }

  /** A canvas font string at the current scale. */
  private fnt(base: number, bold = false): string {
    return `${bold ? "bold " : ""}${this.fs(base)}px Arial`;
  }

  /**
   * Clamped to [0.85, 1.7]. Below that the hour row stops being legible at all;
   * above it a device photo plus three text lines stops fitting its group cell.
   * Lane height scales along with the type, so the ceiling is about DENSITY —
   * how much timeline fits on screen — not about overflow. The view stays
   * correct past it, just very tall.
   */
  private measureTypeScale(): void {
    const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    this.ts = Math.max(0.85, Math.min(1.7, root / 16));
    this.laneH = Math.round(LANE_H_BASE * this.ts);
    this.rulerH = Math.round(RULER_H_BASE * this.ts);
    this.chipH = Math.round(CHIP_H_BASE * this.ts);
    this.img = Math.round(IMG_BASE * this.ts);
  }
  private rafPending = false;
  private readonly imgCache = new Map<string, HTMLImageElement>();
  private measureCtx: CanvasRenderingContext2D | null = null;
  private dprCleanup: (() => void) | null = null;
  /** Last device-pixel ratio applied to the canvas — the backing store is a
   *  separate quantity from the layout box and only this tracks it. */
  private lastDpr = 1;
  // OMON-290: re-measure on container resize — window resizes AND browser
  // zoom (which changes the container's CSS-pixel width). Without it the
  // stage kept its mount-time width forever and overflow-hidden cropped the
  // most recent data off the right edge after any zoom.
  // OMON-293: owned SOLELY by watchSize(). See the ownership rule there.
  private resizeObs: ResizeObserver | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /** True while a drag is in progress or a gesture hasn't settled yet — the
   *  view uses this to hold the 30s live-poll redraw off mid-interaction
   *  (2026-07-08 perf review D2). */
  isInteracting(): boolean {
    return this.dragging || this.settleTimer !== null;
  }

  destroy(): void {
    if (this.viewChangeTimer !== null) {
      clearTimeout(this.viewChangeTimer);
      this.viewChangeTimer = null;
    }
    if (this.settleTimer !== null) {
      clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
    this.contentGroup = null;
    this.overlayGroup = null;
    if (this.dprCleanup) {
      this.dprCleanup();
      this.dprCleanup = null;
    }
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.stage?.destroy();
    this.stage = null;
    this.layer = null;
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }

  render(
    lanes: RenderLane[],
    range: [Date, Date],
    health: Record<string, DeviceHealth> = {},
    view?: [Date, Date],
  ): void {
    this.destroy();
    this.range = [range[0].getTime(), range[1].getTime()];
    // The range can be far wider than what should open on screen: it includes
    // the archive year behind the detail window. Without `view`, picking
    // "1 day" after the archive loaded opened on the whole year (Ed, 2026-09-25).
    this.view = view
      ? this.clampView(view[0].getTime(), view[1].getTime())
      : ([...this.range] as [number, number]);
    // OMON-296: before buildGroups — the gutter is sized by MEASURING text, so
    // the scale has to be known before anything is measured.
    this.measureTypeScale();
    this.buildGroups(lanes, health);
    this.width = Math.max(this.container.clientWidth || 800, 360);
    this.height = this.contentHeight();

    this.container.style.overflowY = "auto";
    this.container.style.overflowX = "hidden";
    // OMON-290 (Ed): use the window's height, not a fixed fraction — vh is
    // CSS pixels, so this also tracks browser zoom instead of clipping tall
    // lane sets behind a stale cap. 210px ≈ app chrome + view header.
    if (!this.container.style.maxHeight) {
      this.container.style.maxHeight = "calc(100vh - 210px)";
    }

    const stage = new Konva.Stage({
      container: this.container as HTMLDivElement,
      width: this.width,
      height: this.height,
    });
    this.stage = stage;
    this.layer = new Konva.Layer();
    stage.add(this.layer);

    const tip = document.createElement("div");
    Object.assign(tip.style, {
      position: "fixed", zIndex: "9999", pointerEvents: "none", display: "none",
      background: "rgba(17,24,39,0.92)", color: "#fff", padding: "4px 8px",
      borderRadius: "4px", font: `${this.fs(11)}px system-ui, sans-serif`, whiteSpace: "nowrap",
      boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
    });
    document.body.appendChild(tip);
    this.tooltipEl = tip;

    this.attachInteraction();
    this.watchSize();
    this.redraw();
  }

  /**
   * OMON-293: ONE observer owns both size questions.
   *
   * A canvas view has two independent sizes: the **layout box** in CSS pixels
   * (how big it is) and the **backing store** in device pixels (how many real
   * pixels it rasterises into). They move independently — browser zoom changes
   * both; dragging the window to a non-Retina display changes only the ratio;
   * toggling a sidebar changes only the layout box. A canvas that tracks one
   * and not the other is either the wrong size or fuzzy.
   *
   * Observing `device-pixel-content-box` collapses the two into one signal:
   * that measurement is in REAL pixels, so a dpr change fires the same callback
   * a resize does — no media-query dance, nothing to re-arm. Safari lacks it,
   * so we fall back to the CSS content box plus the resolution media query.
   *
   * ☠️ **Ownership rule, learned the hard way:** this method is the ONLY thing
   * that may touch `resizeObs`. The previous split had `watchPixelRatio()`
   * disconnect the observer and never rebuild it, while re-arming itself from
   * its own handler — so the FIRST browser zoom silently froze `this.width`
   * forever. After that, zooming out left the stage narrower than the container
   * (whitespace to the right) and zooming in left it wider (content stranded
   * off the edge). One frozen number, two symptoms that look like two bugs.
   */
  private watchSize(): void {
    this.resizeObs?.disconnect();
    this.lastDpr = window.devicePixelRatio || 1;

    this.resizeObs = new ResizeObserver((entries) => {
      if (!this.stage) return;
      const entry = entries[entries.length - 1];
      if (!entry) return;

      // Backing store first: rasterisation quality is independent of layout,
      // and a stale ratio reads as fuzzy text even at the correct width.
      const dpr = window.devicePixelRatio || 1;
      const dprChanged = Math.abs(dpr - this.lastDpr) > 0.001;
      if (dprChanged) {
        this.lastDpr = dpr;
        this.layer?.getCanvas().setPixelRatio(dpr);
      }

      // OMON-296: a text-size change reflows the page, so it reaches us through
      // this same observer. Third independent quantity on one signal.
      const prevTs = this.ts;
      this.measureTypeScale();
      const typeChanged = this.ts !== prevTs;

      // Take the measurement the entry already carries. Reading clientWidth
      // in here forces a synchronous layout inside the callback — the way to
      // earn "ResizeObserver loop completed with undelivered notifications".
      const w = Math.max(this.cssWidthOf(entry), 360);
      const widthChanged = Math.abs(w - this.width) >= 2;
      if (widthChanged) {
        this.width = w;
        this.stage.width(w);
      }

      // Resize the canvas NOW, rebuild the scene on the settle debounce, so
      // dragging a window edge can't rebuild per pixel and there is never a
      // visibly stranded frame waiting on it. A dpr change is one-shot and
      // must re-rasterise, so it redraws immediately instead.
      // A type change alters every layout dimension, so it rebuilds outright
      // rather than waiting on the settle debounce.
      if (dprChanged || typeChanged) this.redraw();
      else if (widthChanged) this.scheduleSettleRebuild();
    });

    // Feature-detect on the prototype, and keep the try/catch: browsers
    // without the box option reject it at observe() time.
    const hasDevicePixelBox =
      typeof ResizeObserverEntry !== "undefined" &&
      "devicePixelContentBoxSize" in ResizeObserverEntry.prototype;
    if (hasDevicePixelBox) {
      try {
        this.resizeObs.observe(this.container, { box: "device-pixel-content-box" });
        return;
      } catch {
        // fall through to the CSS-box path
      }
    }
    this.resizeObs.observe(this.container);
    this.watchPixelRatio();
  }

  /** CSS-pixel inline size straight off the entry. `contentBoxSize` shipped as
   *  a bare object before it was an array in some engines, so tolerate both;
   *  `contentRect` is the last resort and still avoids a layout read. */
  private cssWidthOf(entry: ResizeObserverEntry): number {
    const box = entry.contentBoxSize as unknown;
    if (Array.isArray(box) && box[0]) return (box[0] as ResizeObserverSize).inlineSize;
    if (box && typeof box === "object" && "inlineSize" in box) {
      return (box as ResizeObserverSize).inlineSize;
    }
    return entry.contentRect.width || 800;
  }

  /**
   * dpr fallback for engines without `device-pixel-content-box` (Safari).
   *
   * A Konva canvas fixes its backing-store resolution when it's created;
   * Chrome/Firefox bump `devicePixelRatio` on zoom but the canvas doesn't
   * re-rasterise, so a zoomed-in view shows a scaled bitmap (fuzzy text +
   * device photos). There is no `dprchange` event — the standard workaround is
   * a resolution media query, which only fires when LEAVING its exact ratio,
   * so it re-arms for the new one after each change.
   *
   * ☠️ Must never touch `resizeObs` — see watchSize().
   */
  private watchPixelRatio(): void {
    if (this.dprCleanup) {
      this.dprCleanup();
      this.dprCleanup = null;
    }
    const dpr = window.devicePixelRatio || 1;
    const mq = window.matchMedia(`(resolution: ${dpr}dppx)`);
    const onChange = () => {
      const ndpr = window.devicePixelRatio || 1;
      this.lastDpr = ndpr;
      this.layer?.getCanvas().setPixelRatio(ndpr);
      this.redraw();
      this.watchPixelRatio();
    };
    mq.addEventListener("change", onChange, { once: true });
    this.dprCleanup = () => mq.removeEventListener("change", onChange);
  }

  /**
   * Refresh data WITHOUT tearing down the stage or resetting the user's
   * zoom/pan (`view`) — the live poll uses this so new readings scroll in.
   */
  updateData(
    lanes: RenderLane[],
    range: [Date, Date],
    health: Record<string, DeviceHealth> = {},
  ): void {
    if (!this.stage) {
      this.render(lanes, range, health);
      return;
    }
    const prevRange = this.range;
    this.range = [range[0].getTime(), range[1].getTime()];
    // Keep the view pinned to the right edge if it was already there (live feel).
    const wasAtRightEdge = Math.abs(this.view[1] - prevRange[1]) < 1000;
    if (wasAtRightEdge) this.view = [this.view[0], this.range[1]];
    // OMON-290: the range can now SHRINK (the archive backfill clamps to the
    // oldest real point when it hits empty pre-provisioning months). A view
    // zoomed out into that whitespace must come back inside the honest range.
    if (this.view[0] < this.range[0] || this.view[1] > this.range[1]) {
      this.view = this.clampView(this.view[0], this.view[1]);
    }
    this.buildGroups(lanes, health);
    this.height = this.contentHeight();
    this.stage.height(this.height);
    this.redraw();
  }

  // ── Data shaping ────────────────────────────────────────────────────

  private buildGroups(lanes: RenderLane[], health: Record<string, DeviceHealth>): void {
    // OMON-290 F2: one pass per lane computes the horizon AND checks
    // monotonicity; the sort runs only when a merge actually broke order.
    // This method fires on every updateData — each 30s poll tick and each
    // backfill chunk — and unconditionally re-sorting a year-length,
    // already-sorted array per lane was the single largest recurring cost.
    let horizon = 0;
    for (const lane of lanes) {
      let sorted = true;
      let prev = -Infinity;
      for (const p of lane.points) {
        const t = p.t.getTime();
        if (t < prev) sorted = false;
        else prev = t;
        if (t > horizon) horizon = t;
      }
      if (!sorted) lane.points.sort((a, b) => a.t.getTime() - b.t.getTime());
    }
    this.horizon = horizon;

    // Group lanes by device, preserving first-seen order.
    const byId = new Map<string, DeviceGroup>();
    for (const lane of lanes) {
      let g = byId.get(lane.deviceId);
      if (!g) {
        g = {
          deviceId: lane.deviceId,
          deviceType: lane.deviceType,
          deviceImage: lane.deviceImage,
          deviceName: lane.deviceName,
          location: lane.topLabel,
          health: health[lane.deviceId] ?? null,
          lanes: [],
        };
        byId.set(lane.deviceId, g);
      }
      g.lanes.push(lane);
    }
    this.groups = [...byId.values()];
    this.computeGutterWidth();
  }

  /**
   * OMON-295: an 8×8 diagonal-stripe tile, built once and reused as a Konva
   * fill pattern. Two parallel segments offset by the tile size make the
   * diagonal continue seamlessly across repeats; a single line would break at
   * every tile edge. White at 55% reads over both the red state fill and, more
   * importantly, in greyscale and under colour-vision deficiency.
   */
  private hatchTile(): HTMLCanvasElement {
    if (this.hatchCanvas) return this.hatchCanvas;
    const c = document.createElement("canvas");
    c.width = 8;
    c.height = 8;
    const g = c.getContext("2d");
    if (g) {
      g.strokeStyle = "rgba(255,255,255,0.55)";
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(-2, 6);
      g.lineTo(6, -2);
      g.moveTo(2, 10);
      g.lineTo(10, 2);
      g.stroke();
    }
    this.hatchCanvas = c;
    return c;
  }

  /** Measure text width with an offscreen 2D context (cached). */
  private measure(text: string, font: string): number {
    if (!this.measureCtx) this.measureCtx = document.createElement("canvas").getContext("2d");
    if (!this.measureCtx) return text.length * 7; // crude fallback
    this.measureCtx.font = font;
    return this.measureCtx.measureText(text).width;
  }

  /** Size the frozen gutter to fit the widest tile's name/location/health line
   *  (so the battery+signal+seen badge never clips), clamped to [MIN, MAX]. */
  private computeGutterWidth(): void {
    let maxText = 0;
    for (const g of this.groups) {
      maxText = Math.max(
        maxText,
        this.measure(g.deviceName, this.fnt(11, true)),
        this.measure(g.location, this.fnt(10)),
        this.measure(this.healthLine(g.health), this.fnt(10)),
      );
    }
    // imgX(8) + photo + gap(8) + text + right pad(14, incl. emoji slack).
    const needed = 8 + this.img + 8 + Math.ceil(maxText) + 14;
    // OMON-296: the CAPS scale too. They bound a content-measured width, so a
    // fixed 280px ceiling on scaled-up text would clip exactly the users who
    // asked for larger text — the clamp would quietly undo the setting.
    this.gutterW = Math.max(
      Math.round(GUTTER_W_MIN * this.ts),
      Math.min(Math.round(GUTTER_W_MAX * this.ts), needed),
    );
  }

  private contentHeight(): number {
    let h = TOP + this.rulerH;
    for (const g of this.groups) {
      h += Math.max(g.lanes.length * (this.laneH + LANE_GAP), this.img + 16) + GROUP_GAP;
    }
    return Math.max(h, 140);
  }

  // ── Geometry ────────────────────────────────────────────────────────

  private plotWidth(): number {
    return this.width - RIGHT - this.gutterW;
  }
  private xTime(ms: number): number {
    const [a, b] = this.view;
    return this.gutterW + ((ms - a) / (b - a)) * this.plotWidth();
  }

  private scheduleRedraw(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.redraw();
    });
  }

  // ── Interaction ─────────────────────────────────────────────────────

  private attachInteraction(): void {
    const stage = this.stage!;
    stage.container().style.cursor = "grab";

    stage.on("wheel", (e) => {
      const we = e.evt as WheelEvent;
      const px = stage.getPointerPosition()?.x ?? this.gutterW;
      if (we.ctrlKey) {
        e.evt.preventDefault();
        this.zoom(px, we.deltaY);
      } else if (Math.abs(we.deltaX) > Math.abs(we.deltaY)) {
        e.evt.preventDefault();
        this.pan(-we.deltaX);
      }
      // else: plain vertical wheel → native container scroll through groups.
    });

    stage.on("mousedown", (e) => {
      // Gutter clicks raise the device filter — don't start a time-drag.
      const did = (e.target as Konva.Node).getAttr("_deviceFilter");
      if (did) {
        this.onDeviceFilter?.(did, stage.getPointerPosition()?.x ?? 0, stage.getPointerPosition()?.y ?? 0);
        return;
      }
      this.dragging = true;
      this.lastPointerX = stage.getPointerPosition()?.x ?? 0;
      stage.container().style.cursor = "grabbing";
      this.hideTip();
    });
    stage.on("mousemove", (e) => {
      if (this.dragging) {
        const px = stage.getPointerPosition()?.x ?? this.lastPointerX;
        this.pan(px - this.lastPointerX);
        this.lastPointerX = px;
        this.hideTip();
        return;
      }
      const node = e.target as Konva.Node;
      const pos = stage.getPointerPosition();
      const tip = pos ? this.tipAt(pos.x, pos.y) : null;
      if (tip) this.showTip(tip, e.evt);
      else this.hideTip();
      stage.container().style.cursor = node.getAttr("_deviceFilter") ? "pointer" : "grab";
    });
    stage.on("touchstart", (e) => {
      const did = (e.target as Konva.Node).getAttr("_deviceFilter");
      if (did) {
        this.onDeviceFilter?.(did, stage.getPointerPosition()?.x ?? 0, stage.getPointerPosition()?.y ?? 0);
        return;
      }
      this.dragging = true;
      this.lastPointerX = stage.getPointerPosition()?.x ?? 0;
      const pos = stage.getPointerPosition();
      const tip = pos ? this.tipAt(pos.x, pos.y) : null;
      if (tip) this.showTip(tip, e.evt);
      else this.hideTip();
    });
    stage.on("touchmove", () => {
      if (!this.dragging) return;
      const px = stage.getPointerPosition()?.x ?? this.lastPointerX;
      this.pan(px - this.lastPointerX);
      this.lastPointerX = px;
      this.hideTip();
    });
    const endDrag = () => {
      this.dragging = false;
      if (this.stage) this.stage.container().style.cursor = "grab";
    };
    stage.on("mouseup touchend", endDrag);
    stage.on("mouseleave", () => {
      endDrag();
      this.hideTip();
    });
  }

  private showTip(text: string, evt: MouseEvent | TouchEvent): void {
    const el = this.tooltipEl;
    if (!el) return;
    const pt = "touches" in evt ? (evt.changedTouches[0] ?? evt.touches[0]) : evt;
    if (!pt) return;
    el.textContent = text;
    el.style.display = "block";
    // Edge-flip: the tip normally sits lower-right of the cursor, but at the
    // viewport's right/bottom edge that clips it — badly when you're zoomed and
    // hovering the swimlane's right edge. Measure it now it's visible and flip to
    // the left of / above the cursor when it would overflow; clamp so it never
    // leaves the viewport on the opposite side either.
    const pad = 14;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = pt.clientX + pad;
    if (left + w > window.innerWidth - 4) left = pt.clientX - pad - w;
    if (left < 4) left = 4;
    let top = pt.clientY + pad;
    if (top + h > window.innerHeight - 4) top = pt.clientY - pad - h;
    if (top < 4) top = 4;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }
  private hideTip(): void {
    if (this.tooltipEl) this.tooltipEl.style.display = "none";
  }

  /** First index in `pts` with time >= t (pts sorted ascending). */
  private static lowerBound(pts: RenderPoint[], t: number): number {
    let lo = 0;
    let hi = pts.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (pts[mid].t.getTime() < t) lo = mid + 1;
      else hi = mid;
    }
    return lo;
  }

  /**
   * Analytic hover: resolve (px, py) → tooltip text with lane arithmetic + a
   * binary search over the lane's sorted points. Replaces Konva hit-canvas
   * tooltips — bands/dots are `listening:false` now, so the whole scene draws
   * once instead of twice, and tooltip strings are built only when hovered.
   */
  private tipAt(px: number, py: number): string | null {
    if (px < this.gutterW || px > this.width - RIGHT) return null;
    let slot: { lane: RenderLane; y: number } | null = null;
    for (const s of this.laneSlots) {
      if (py >= s.y && py < s.y + this.laneH) {
        slot = s;
        break;
      }
    }
    if (!slot) return null;
    const lane = slot.lane;
    const pts = lane.points;
    if (pts.length === 0) return null;
    const [a, b] = this.view;
    const msPerPx = (b - a) / this.plotWidth();
    const t = a + (px - this.gutterW) * msPerPx;

    if (lane.kind === "event") {
      // Nearest dot within the dot radius (+1px slack).
      const tol = 6 * msPerPx;
      const i = TimelineRenderer.lowerBound(pts, t);
      let best: RenderPoint | null = null;
      let bestD = Infinity;
      for (const cand of [pts[i - 1], pts[i]]) {
        if (!cand) continue;
        const d = Math.abs(cand.t.getTime() - t);
        if (d < bestD) {
          bestD = d;
          best = cand;
        }
      }
      if (!best || bestD > tol) return null;
      return `${lane.subLabel} · ${fmtStamp(best.t)}`;
    }

    // Band lanes: the reading in effect at time t is the last point <= t; the
    // band ends at the next reading (or the forward-fill horizon for the final
    // run — matching drawLane's fillEnd exactly).
    const idx = TimelineRenderer.lowerBound(pts, t + 1) - 1;
    if (idx < 0) return null;
    const isLast = idx === pts.length - 1;
    const fillEnd =
      lane.kind === "state" ? Math.max(this.horizon, Date.now()) : this.horizon;
    let end = isLast ? fillEnd : pts[idx + 1].t.getTime();
    // Inside a silence the gradient band has stopped short (drawLane), so
    // there is no reading to report there either.
    if (lane.kind === "gradient" && !isLast && isSilence(pts[idx].t.getTime(), end)) {
      end = bandEndBeforeGap(pts[idx].t.getTime(), idx >= 1 ? pts[idx - 1].t.getTime() : null);
    }
    if (t > end) return null; // past the horizon, or in a gap (gradient lanes)
    const endLabel = fmtHour(new Date(end));
    return `${lane.subLabel} · ${lane.valueLabel(pts[idx].value)} · ${fmtStamp(pts[idx].t)}–${endLabel}`;
  }

  private clampView(a: number, b: number): [number, number] {
    const [lo, hi] = this.range;
    const fullSpan = hi - lo;
    const span = Math.max(MIN_SPAN_MS, Math.min(fullSpan, b - a));
    let na = a;
    let nb = a + span;
    if (na < lo) { na = lo; nb = lo + span; }
    if (nb > hi) { nb = hi; na = hi - span; }
    return [na, nb];
  }

  private zoom(px: number, deltaY: number): void {
    const frac = Math.max(0, Math.min(1, (px - this.gutterW) / this.plotWidth()));
    const [a, b] = this.view;
    const span = b - a;
    const cursor = a + frac * span;
    const newSpan = span * Math.exp(deltaY * 0.0015);
    this.view = this.clampView(cursor - frac * newSpan, cursor - frac * newSpan + newSpan);
    this.applyViewTransform();
    this.scheduleSettleRebuild();
    this.scheduleViewChange();
  }

  /** Debounced: notify the view that the visible span settled (zoom/pan), so it
   *  can re-fetch at a matched resolution (OMON-222). */
  private scheduleViewChange(): void {
    if (!this.onViewChange) return;
    if (this.viewChangeTimer !== null) clearTimeout(this.viewChangeTimer);
    this.viewChangeTimer = window.setTimeout(() => {
      this.viewChangeTimer = null;
      this.onViewChange?.(new Date(this.view[0]), new Date(this.view[1]));
    }, 350);
  }

  private pan(dxPixels: number): void {
    const [a, b] = this.view;
    const span = b - a;
    const dms = -dxPixels * (span / this.plotWidth());
    this.view = this.clampView(a + dms, b + dms);
    this.applyViewTransform();
    this.scheduleSettleRebuild();
    this.scheduleViewChange();
  }

  /**
   * Gesture-frame path: reposition/scale the cached content bitmap for the new
   * view and refresh only the overlay (ruler + now marker). No node rebuild —
   * this replaced the destroy-everything redraw that ran at ~5fps on dense
   * views (2026-07-08 perf review R1/R3).
   */
  private applyViewTransform(): void {
    const g = this.contentGroup;
    const layer = this.layer;
    if (!g || !layer) {
      this.scheduleRedraw();
      return;
    }
    const [ba] = this.builtView;
    const [a, b] = this.view;
    const sx = (this.builtView[1] - ba) / (b - a);
    // Content was built at stage-x = gutterW + (ms - builtView0) * builtPxPerMs.
    // Solve g.x so ms lands at gutterW + (ms - view0) * builtPxPerMs * sx.
    g.scaleX(sx);
    g.x(this.gutterW * (1 - sx) - this.builtPxPerMs * sx * (a - ba));
    this.redrawOverlay();
    if (!this.rafPending) {
      this.rafPending = true;
      requestAnimationFrame(() => {
        this.rafPending = false;
        layer.batchDraw();
      });
    }
  }

  /** Rebuild the content at the settled view: dots un-stretch, gradient
   *  palettes re-normalize to the visible range, ruler density re-thins. */
  private scheduleSettleRebuild(): void {
    if (this.settleTimer !== null) clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(() => {
      this.settleTimer = null;
      this.redraw();
    }, 250);
  }

  // ── Image loading ───────────────────────────────────────────────────

  private getImage(url: string): HTMLImageElement | null {
    const cached = this.imgCache.get(url);
    if (cached) return cached.complete && cached.naturalWidth > 0 ? cached : null;
    const img = new Image();
    // OMON-220: NO crossOrigin. The Firebase Storage download URLs don't send
    // CORS headers, so `crossOrigin="anonymous"` makes the load fail (→ blank
    // gutter). A plain cross-origin <img> loads fine and Konva renders it; it
    // only taints the main canvas, which is harmless here (we never export it,
    // and hit-detection uses a separate solid-colour hit canvas).
    img.onload = () => this.scheduleRedraw();
    img.onerror = () => {}; // gutter falls back to the initial
    img.src = url;
    this.imgCache.set(url, img);
    return null;
  }

  // ── Drawing ─────────────────────────────────────────────────────────

  private redraw(): void {
    const layer = this.layer;
    if (!layer) return;
    const _t0 = performance.now();
    layer.destroyChildren();
    this.contentGroup = null;
    this.overlayGroup = null;

    if (this.groups.length === 0) {
      layer.add(new Konva.Text({
        x: this.gutterW, y: TOP + this.rulerH + 8, text: "No readings in this window.",
        fontSize: this.fs(12), fill: "#9ca3af",
      }));
      layer.draw();
      return;
    }

    // Content is built around the current view at the current scale, so
    // subsequent pans/zooms are transforms of the cached bitmap.
    this.builtView = [...this.view] as [number, number];
    this.builtPxPerMs = this.plotWidth() / (this.view[1] - this.view[0]);
    const viewSpan = this.view[1] - this.view[0];
    this.builtRange = [
      Math.max(this.range[0], this.view[0] - viewSpan),
      Math.min(this.range[1], this.view[1] + viewSpan),
    ];

    const bgGroup = new Konva.Group({ listening: false });
    const clipGroup = new Konva.Group({
      clipX: this.gutterW, clipY: 0,
      clipWidth: this.plotWidth(), clipHeight: this.height,
      listening: false,
    });
    const contentGroup = new Konva.Group({ listening: false });
    clipGroup.add(contentGroup);
    const gutterGroup = new Konva.Group();
    const overlayGroup = new Konva.Group({ listening: false });
    layer.add(bgGroup, clipGroup, gutterGroup, overlayGroup);
    this.contentGroup = contentGroup;
    this.overlayGroup = overlayGroup;

    this.drawDayGrid(contentGroup, this.height);

    let y = TOP + this.rulerH;
    this.laneSlots = [];
    for (const g of this.groups) {
      const groupH = Math.max(g.lanes.length * (this.laneH + LANE_GAP), this.img + 16);
      for (let i = 0; i < g.lanes.length; i++) {
        const laneY = y + i * (this.laneH + LANE_GAP);
        this.laneSlots.push({ lane: g.lanes[i], y: laneY });
        this.drawLane(bgGroup, contentGroup, g.lanes[i], laneY);
        // Into the FROZEN group, not bg — the chip has to sit above the bands.
        this.drawLaneChip(gutterGroup, g.lanes[i], laneY);
      }
      this.drawGutter(gutterGroup, g, y, groupH);
      y += groupH + GROUP_GAP;
    }
    this.redrawOverlay();

    // Rasterise the content group: pan/zoom then draw ONE bitmap instead of
    // thousands of shapes. Re-cached on every rebuild; capped pixelRatio keeps
    // the bitmap memory bounded on hi-dpi screens. Konva throws on caching a
    // zero-size group (a client with no readings yet) — skip the cache then;
    // an uncached empty group costs nothing anyway.
    const bbox = contentGroup.getClientRect({ skipTransform: true });
    if (bbox.width >= 1 && bbox.height >= 1) {
      contentGroup.cache({ pixelRatio: Math.min(window.devicePixelRatio || 1, 2) });
    }

    // OMON-245 diagnostic: surface the real rebuild cost + node count, so we
    // know if the bottleneck is node count vs the stage teardown render() adds.
    const _ms = performance.now() - _t0;
    if (_ms > 150) console.debug(`[connect] rebuild ${Math.round(_ms)}ms · ${layer.find("Shape").length} nodes`);
    layer.draw();
  }

  /** Ruler + now marker: tiny, view-dependent — redrawn every gesture frame. */
  private redrawOverlay(): void {
    const overlay = this.overlayGroup;
    if (!overlay) return;
    overlay.destroyChildren();
    this.drawNowMarker(overlay, this.height);
    this.drawRuler(overlay);
  }

  /** Subtle full-height vertical lines at each local midnight. Drawn into the
   *  CONTENT group across the full loaded range (they pan with the data); the
   *  clip group hides anything outside the plot area. OMON-290: the grid thins
   *  with the view span the same way the ruler labels do — at year-wide zoom a
   *  line per midnight is ~3px apart and reads as a grey wash, not a grid. */
  private drawDayGrid(content: Konva.Group, bottom: number): void {
    const [a, b] = this.builtRange;
    const spanDays = (this.view[1] - this.view[0]) / DAY_MS;
    const line = (ms: number) =>
      content.add(new Konva.Line({
        points: [this.xTime(ms), TOP + this.rulerH, this.xTime(ms), bottom],
        stroke: "#f3f4f6", strokeWidth: 1, listening: false,
      }));
    if (spanDays > 120) {
      const first = new Date(a);
      let m = new Date(first.getFullYear(), first.getMonth(), 1);
      if (m.getTime() < a) m = new Date(first.getFullYear(), first.getMonth() + 1, 1);
      for (; m.getTime() <= b; m = new Date(m.getFullYear(), m.getMonth() + 1, 1)) {
        line(m.getTime());
      }
      return;
    }
    const stepDays = spanDays > 24 ? 7 : 1;
    for (let d = startOfDay(new Date(a)).getTime(); d <= b; d += stepDays * DAY_MS) {
      if (d < a) continue;
      line(d);
    }
  }

  /** OMON-227: a live "now" marker at the current time — a dashed accent line + "now"
   *  tag at the right edge whenever the view is pinned live (the right edge is
   *  ~now). Suppressed when the user pans into the past (now is off-screen
   *  right). This is the unambiguous "you're seeing up to this instant" cue, and
   *  it advances every redraw as the live poll moves the window forward. */
  private drawNowMarker(layer: Konva.Group, bottom: number): void {
    const now = Date.now();
    const [a, b] = this.view;
    // Only when the right edge is live (within ~2 min of now); a past-panned
    // window has no "now" inside it.
    if (now < a || b < now - 2 * 60_000) return;
    const x = this.xTime(Math.min(now, b));
    if (x < this.gutterW || x > this.width - RIGHT) return;
    layer.add(new Konva.Line({
      points: [x, TOP + this.rulerH, x, bottom], stroke: "#dc2626", strokeWidth: 1,
      dash: [3, 3], opacity: 0.65, listening: false,
    }));
    layer.add(new Konva.Text({
      x: x - this.fs(32), y: TOP + this.fs(14), width: this.fs(30), align: "right",
      text: "now", fontSize: this.fs(8), fontStyle: "bold", fill: "#dc2626", listening: false,
    }));
  }

  private drawRuler(layer: Konva.Group): void {
    const [a, b] = this.view;
    const span = b - a;
    const right = this.width - RIGHT;
    layer.add(new Konva.Line({
      points: [this.gutterW, TOP + this.rulerH - 1, right, TOP + this.rulerH - 1],
      stroke: "#e5e7eb", strokeWidth: 1, listening: false,
    }));

    // OMON-294: the interval is chosen by what FITS, not by the span. The old
    // ladder (spanDays > 48 ? 14 : > 24 ? 7 : …) was tuned at ~1500px, so a
    // narrower window got the same label density and ran them together.
    const plotW = right - this.gutterW;
    const { ticks, fmt } = this.rulerPlan(a, b, plotW);

    // The hour row is gated by FIT, not by a span threshold (Ed 2026-08-28:
    // a 3.5-day view had room for 3-hour labels but the old `span <= 2 days`
    // gate kept the row dark — at that zoom "when in the day" was exactly
    // the missing information). The ladder picks the finest step whose
    // labels clear each other; when not even 12-hour labels fit (deep
    // archive spans), there is no step and the row stays off — the same
    // fits-first rule as everything else on this ruler.
    // (2026-08-26 history: both tiers near-black, hierarchy by size —
    // 10px dates over 11px hours.)
    const hourMaxLabels = Math.max(
      2,
      Math.floor(plotW / (this.measure("00:00", this.fnt(11, true)) + HOUR_GAP)),
    );
    const hourStepMin = TIME_STEPS_MIN.find(
      (m) => span / 60_000 / m <= hourMaxLabels,
    );
    const dayFs = 10;
    const dayY = 3;

    // Place a label if it clears everything already placed. Collision is tested
    // on real label BOXES, not tick positions, because a label pulled inside
    // the right edge no longer sits where its tick does.
    //
    // ☠️ Dedupe by TEXT as well as by box: when the zoom hands rulerPlan
    // sub-day ticks, fmtDay prints the SAME date for every tick on that day —
    // Ed's screenshot showed "Thu 20 Aug" four times across one screen. A
    // day's name only carries information ONCE.
    // A SET, not a last-seen chain: the newest day is force-placed before the
    // ascending tick loop, so today's other sub-day ticks arrive non-adjacent.
    const placed: { x0: number; x1: number }[] = [];
    const placedTexts = new Set<string>();
    const place = (t: number, x: number): void => {
      const text = fmt(new Date(t));
      if (placedTexts.has(text)) return;
      const w = this.measure(text, this.fnt(dayFs, true));
      // The rightmost label used to run past the stage and get cut ("Wed 12 A").
      // Pull it inside instead. This fires on most renders, not just edge cases:
      // the newest day is forced below, so there is always a label there.
      const x0 = Math.min(x + 3, right - w - 2);
      const x1 = x0 + w;
      if (placed.some((p) => x0 < p.x1 + RULER_GAP && p.x0 < x1 + RULER_GAP)) return;
      layer.add(new Konva.Text({
        x: x0, y: TOP + this.fs(dayY), text,
        fontSize: this.fs(dayFs), fontStyle: "bold",
        fill: "#111827", listening: false,
      }));
      placed.push({ x0, x1 });
      placedTexts.add(text);
    };

    // OMON-227, preserved deliberately: the newest day in view keeps its label
    // whatever interval is chosen, or a live view reads as stale ("Fri 26"
    // while it is really Sat 27). Calendar-anchored ticks can't guarantee that
    // on their own, so it is drawn FIRST and claims its space — the generic
    // ticks yield to it rather than the other way round. Not at month scale,
    // where a stray day label beside "Aug 2026" is just noise.
    if (fmt !== fmtMonth) {
      const today = startOfDay(new Date(b)).getTime();
      const x = this.xTime(today);
      if (today >= a && x >= this.gutterW - 1 && x <= right) place(today, x);
    }

    // The fit only has to get close; `place` is what guarantees the result, at
    // widths nobody anticipated.
    for (const t of ticks) {
      const x = this.xTime(+t);
      if (x < this.gutterW - 1 || x > right) continue;
      place(+t, x);
    }

    // Hour row — the fine tier under the day/month tier, whenever a ladder
    // step fits (gate computed above with the day-row constants).
    if (hourStepMin !== undefined) {
      // Ed 2026-08-26: at this zoom the TIMES are what you came to read —
      // they take the bold dark style the day row just gave up. (They were
      // 8px in blue-200, which on the ruler ground was barely visible at
      // all — the inverse of the hierarchy a zoomed-in reader needs.)
      const stepMs = hourStepMin * 60_000;
      for (let t = Math.ceil(a / stepMs) * stepMs; t <= b; t += stepMs) {
        const x = this.xTime(t);
        if (x < this.gutterW || x > right) continue;
        layer.add(new Konva.Text({
          x: x - this.fs(20), y: TOP + this.fs(17), width: this.fs(40), align: "center",
          text: fmtHour(new Date(t)), fontSize: this.fs(11), fontStyle: "bold",
          fill: "#111827", listening: false,
        }));
      }
    }
  }

  /**
   * OMON-294: how many ruler labels fit, and in what format.
   *
   * `timeTicks` from d3-time is the accepted answer to "where do ticks go" — a
   * fixed ladder of calendar-aligned intervals (…1/2 day, 1 week, 1/3 month, 1
   * year) from which it picks the finest that yields about `count` ticks. Being
   * calendar-anchored is the point: ticks land on midnight and the 1st, so they
   * cannot drift with the view the way counted-back steps can.
   *
   * All this method decides is `count`, from plot width ÷ measured label width.
   * Two passes, because the format depends on the interval and the interval
   * depends on the count: seed with the widest label we might draw (so the
   * first guess can only be too sparse, never too dense), then re-fit against
   * the format actually chosen.
   */
  private rulerPlan(
    a: number,
    b: number,
    plotW: number,
  ): { ticks: Date[]; fmt: (d: Date) => string; labelW: number } {
    const sample = (f: (d: Date) => string) => this.measure(f(new Date(b)), this.fnt(10, true));
    let labelW = Math.max(sample(fmtDay), sample(fmtMonth)) + RULER_GAP;
    let ticks: Date[] = [];
    let fmt = fmtDay;
    for (let pass = 0; pass < 2; pass++) {
      const count = Math.max(2, Math.floor(plotW / labelW));
      ticks = timeTicks(new Date(a), new Date(b), count);
      // Read the granularity off the spacing d3 actually returned rather than
      // asking for the interval object — the step is what decides whether a
      // day label still carries meaning.
      const stepMs = ticks.length >= 2 ? +ticks[1] - +ticks[0] : b - a;
      fmt = stepMs >= 27 * DAY_MS ? fmtMonth : fmtDay;
      labelW = sample(fmt) + RULER_GAP;
    }
    return { ticks, fmt, labelW };
  }

  /** The frozen device gutter cell: photo, name, location, health badge. */
  private drawGutter(layer: Konva.Group, g: DeviceGroup, y: number, h: number): void {
    // Opaque cell so bands clamped at the gutter edge never bleed under it.
    const cell = new Konva.Rect({
      x: 0, y, width: this.gutterW, height: h, fill: "#fff",
      stroke: "#f3f4f6", strokeWidth: 1,
    });
    cell.setAttr("_deviceFilter", g.deviceId);
    layer.add(cell);

    const imgX = 8;
    const imgY = y + 8;
    const url = g.deviceImage;
    const loaded = url ? this.getImage(url) : null;
    if (loaded) {
      const im = new Konva.Image({ x: imgX, y: imgY, image: loaded, width: this.img, height: this.img, cornerRadius: 6 });
      im.setAttr("_deviceFilter", g.deviceId);
      layer.add(im);
    } else {
      // Placeholder tile + initial while the photo loads / if absent.
      const ph = new Konva.Rect({ x: imgX, y: imgY, width: this.img, height: this.img, cornerRadius: 6, fill: "#f3f4f6", stroke: "#eff6ff", strokeWidth: 1 });
      ph.setAttr("_deviceFilter", g.deviceId);
      layer.add(ph);
      layer.add(new Konva.Text({
        x: imgX, y: imgY + this.img / 2 - this.fs(9), width: this.img, align: "center",
        text: (g.deviceName[0] ?? "?").toUpperCase(), fontSize: this.fs(18), fontStyle: "bold", fill: "#9ca3af",
        listening: false,
      }));
    }

    const textX = imgX + this.img + 8;
    const textW = this.gutterW - textX - 6;
    layer.add(new Konva.Text({
      x: textX, y: y + this.fs(9), width: textW, text: g.deviceName,
      fontSize: this.fs(11), fontStyle: "bold", fill: "#374151", ellipsis: true, wrap: "none", listening: false,
    }));
    layer.add(new Konva.Text({
      x: textX, y: y + this.fs(23), width: textW, text: g.location,
      fontSize: this.fs(10), fill: "#6b7280", ellipsis: true, wrap: "none", listening: false,
    }));
    layer.add(new Konva.Text({
      x: textX, y: y + this.fs(37), width: textW, text: this.healthLine(g.health),
      fontSize: this.fs(10), fill: "#6b7280", ellipsis: true, wrap: "none", listening: false,
    }));
  }

  /**
   * OMON-292: the sticky lane-name chip.
   *
   * The channel name used to be drawn into the BACKGROUND group — i.e. below
   * the data — so it survived only on sparse event lanes ("Emergency button",
   * "Push sent") and was painted over completely by the unbroken RLE bands of
   * every dense lane: precisely the lanes where knowing which sensor you are
   * looking at matters most (Ed, 2026-08-12).
   *
   * It now draws into the frozen group, which is painted ABOVE the clipped
   * content and takes no view transform — so the chip is sticky by
   * construction. It cannot pan away with the data, and unlike the overlay
   * (destroyed and rebuilt every gesture frame) it costs nothing to hold still.
   *
   * Inset is impossible by design: a band's x-position IS the time axis, shared
   * with the ruler, so reserving space inside the plot would desync the two.
   * The chip therefore floats, and the scrim is what keeps that honest — the
   * band reads through it as a pastel of itself instead of being erased.
   */
  private drawLaneChip(layer: Konva.Group, lane: RenderLane, y: number): void {
    // OMON-295: a heatmap with no key can be read for PATTERN but not for
    // VALUE. The palette is stretched to the visible extremes, so those two
    // numbers are the whole legend — and stating them is what stops the same
    // blue meaning different things in two lanes, or at two zoom levels.
    const range = this.laneRange.get(lane);
    const text = range ? `${lane.subLabel} · ${fmtRange(range[0], range[1])}` : lane.subLabel;
    const maxTextW = Math.max(40, this.plotWidth() * CHIP_MAX_FRAC);
    const textW = Math.min(
      Math.ceil(this.measure(text, this.fnt(CHIP_FONT))) + 1,
      maxTextW,
    );
    const scrimW = CHIP_PAD + textW + CHIP_FADE;
    // Flush to the gutter edge, so no sliver of band shows to the text's left.
    layer.add(new Konva.Rect({
      x: this.gutterW, y: y + 1, width: scrimW, height: this.chipH,
      fillLinearGradientStartPoint: { x: 0, y: 0 },
      fillLinearGradientEndPoint: { x: scrimW, y: 0 },
      fillLinearGradientColorStops: [
        0, CHIP_SCRIM,
        (CHIP_PAD + textW) / scrimW, CHIP_SCRIM,
        1, "rgba(255,255,255,0)",
      ],
      listening: false,
    }));
    layer.add(new Konva.Text({
      x: this.gutterW + CHIP_PAD, y: y + this.fs(4), width: textW,
      text, fontSize: this.fs(CHIP_FONT), fill: "#374151",
      ellipsis: true, wrap: "none", listening: false,
    }));
  }

  /** Compose the one-line health badge: battery / signal / last-seen. */
  private healthLine(health: DeviceHealth | null): string {
    if (!health) return "—";
    const parts: string[] = [];
    if (health.battery !== null) parts.push(`🔋 ${health.battery}%`);
    if (health.rssi !== null) parts.push(`📶 ${health.rssi}`);
    if (health.lastSeen) parts.push(ago(health.lastSeen, Date.now()));
    return parts.length ? parts.join("  ") : "—";
  }

  /**
   * OMON-223: per-point colours for a gradient lane, auto-scaled to the data
   * VISIBLE in the current view. min/max are taken over points inside `view`
   * (falling back to the whole series if none are visible), then each value is
   * interpolated continuously through `gradientColor`. Recomputed every redraw,
   * so zooming/panning re-stretches the palette to whatever's on screen. A flat
   * series (min==max) maps to the mid colour; nulls stay grey.
   */
  private gradientBandColors(pts: RenderPoint[], palette: PaletteName): string[] {
    const [a, b] = this.view;
    let lo = Infinity;
    let hi = -Infinity;
    // Scan only the visible slice (binary-searched bounds), not the full series.
    const from = TimelineRenderer.lowerBound(pts, a);
    const to = TimelineRenderer.lowerBound(pts, b + 1);
    for (let i = from; i < to; i++) {
      const p = pts[i];
      if (p.value === null) continue;
      if (p.value < lo) lo = p.value;
      if (p.value > hi) hi = p.value;
    }
    if (lo === Infinity) {
      // Nothing in view (sparse lane) — scale to the full loaded series instead.
      for (const p of pts) {
        if (p.value === null) continue;
        if (p.value < lo) lo = p.value;
        if (p.value > hi) hi = p.value;
      }
    }
    // Colour mapping is O(points); reuse it while (data, lo, hi) are unchanged —
    // polls/filter toggles redraw with identical extremes far more often than not.
    const cached = this.gradientCache.get(pts);
    if (cached && cached.lo === lo && cached.hi === hi) return cached.colors;
    const span = hi - lo;
    const colors = pts.map((p) =>
      p.value === null
        ? GRADIENT_NULL
        : span > 0
          ? paletteColor(palette, (p.value - lo) / span)
          : paletteColor(palette, 0.5),
    );
    this.gradientCache.set(pts, { lo, hi, colors });
    return colors;
  }

  private drawLane(bg: Konva.Group, content: Konva.Group, lane: RenderLane, y: number): void {
    const right = this.width - RIGHT;
    // Backgrounds are view-independent → the static bg group (they don't pan;
    // the content group above them does). The channel label used to live here
    // too — see drawLaneChip for why it no longer can.
    bg.add(new Konva.Rect({
      x: this.gutterW, y, width: right - this.gutterW, height: this.laneH, fill: "#f9fafb", stroke: "#f3f4f6", strokeWidth: 1, listening: false,
    }));

    const pts = lane.points;
    if (lane.kind === "event") {
      const cy = y + this.laneH / 2;
      // Only the points inside the content bitmap's coverage (binary-searched
      // bounds); the clip group handles edge visibility. Tooltips are analytic
      // (tipAt), so dots are non-listening — no hit-canvas double-draw and no
      // eager tooltip strings.
      const [ra, rb] = this.builtRange;
      const from = TimelineRenderer.lowerBound(pts, ra);
      const to = TimelineRenderer.lowerBound(pts, rb + 1);
      for (let i = from; i < to; i++) {
        const p = pts[i];
        content.add(new Konva.Circle({
          x: this.xTime(p.t.getTime()), y: cy, radius: 5, fill: lane.colorFor(p.value), stroke: "#fff",
          strokeWidth: 1, opacity: 0.9, listening: false, perfectDrawEnabled: false,
        }));
      }
      return;
    }

    // RLE bands over the CONTINUOUS axis. Each run extends to the next reading's
    // time; the final open run stops at the data horizon (never the future).
    //
    // OMON-290 F3: colours and the run loop operate on the builtRange SLICE,
    // not the whole series — zoomed to a week over a year of archive, the old
    // full-series pass did 50× more colour work than the screen could show.
    // One point of margin each side keeps run continuity across the edges
    // (a band that starts before the range still paints its visible part).
    // When the slice IS the whole series (the common fresh-mount case), the
    // original array is used so the identity-keyed colour caches still hit.
    const [sra, srb] = this.builtRange;
    let segFrom = TimelineRenderer.lowerBound(pts, sra) - 1;
    if (segFrom < 0) segFrom = 0;
    let segTo = TimelineRenderer.lowerBound(pts, srb + 1) + 1;
    if (segTo > pts.length) segTo = pts.length;
    const isFullSlice = segFrom === 0 && segTo === pts.length;
    const seg = isFullSlice ? pts : pts.slice(segFrom, segTo);

    // OMON-223: gradient (continuous) lanes auto-scale the palette to the
    // VISIBLE data range and interpolate smoothly — so small variations show at
    // any zoom (replacing the 4-bucket `thermal()` Flutter fossil + its fixed
    // per-sensor domains). State/event lanes keep their fixed colour mapping.
    let colors: string[];
    if (lane.kind === "gradient") {
      colors = this.gradientBandColors(seg, lane.palette);
      // OMON-295: remember the extremes the palette was stretched over, so the
      // lane chip can state them. Auto-scaling to the VISIBLE data (OMON-223)
      // is what makes small variation legible, but it also means a colour has
      // no fixed meaning across lanes or zoom levels — the chip is where that
      // stops being a hidden assumption.
      const ex = this.gradientCache.get(seg);
      if (ex && isFinite(ex.lo) && isFinite(ex.hi)) this.laneRange.set(lane, [ex.lo, ex.hi]);
    } else if (isFullSlice) {
      // State colours depend only on the data — compute once per points array.
      const cached = this.stateColorCache.get(pts);
      if (cached) {
        colors = cached;
      } else {
        colors = pts.map((p) => lane.colorFor(p.value));
        this.stateColorCache.set(pts, colors);
      }
    } else {
      // A zoomed slice is bounded work by construction; identity caching
      // would key on a throwaway array, so skip it.
      colors = seg.map((p) => lane.colorFor(p.value));
    }
    // State lanes (door/window, plug output, mats) only emit ON CHANGE, so the
    // last reading's state still holds NOW — forward-fill the final band to the
    // current time rather than stopping at the data horizon, so a sensor that's
    // "quiet because nothing changed" doesn't read as missing data. Continuous
    // (gradient) lanes must NOT do this: "now"'s temperature is genuinely unknown
    // until the next sample, so they stop at the horizon. (OMON-232)
    const fillEnd =
      lane.kind === "state" ? Math.max(this.horizon, Date.now()) : this.horizon;
    // OMON-290 F3: the forward-fill only applies when the slice actually
    // contains the series' true last point; a mid-history slice caps its final
    // run at the first point beyond it instead.
    const segEndCap = segTo < pts.length ? pts[segTo].t.getTime() : fillEnd;
    // A gradient run also breaks at a silence, and the band before it stops
    // short, leaving background: no colour means no data (band-gaps.ts).
    const gapAware = lane.kind === "gradient";
    for (let k = 1, runStart = 0; k <= seg.length; k++) {
      const silent =
        gapAware && k < seg.length && isSilence(seg[k - 1].t.getTime(), seg[k].t.getTime());
      if (k === seg.length || silent || colors[k] !== colors[runStart]) {
        const tStart = seg[runStart].t.getTime();
        const isFinalRun = k === seg.length;
        const tEnd = isFinalRun
          ? segEndCap
          : silent
            ? bandEndBeforeGap(seg[k - 1].t.getTime(), k >= 2 ? seg[k - 2].t.getTime() : null)
            : seg[k].t.getTime();
        // Clamp runs to the content bitmap's coverage (a run crossing its edge
        // renders partially); runs wholly outside emit nothing. Plot-area edge
        // visibility is the clip group's job.
        const [ra, rb] = this.builtRange;
        const px = this.xTime(Math.max(tStart, ra));
        const pxEnd = this.xTime(Math.min(tEnd, rb));
        if (pxEnd > px) {
          // OMON-245: fill-only band — disable Konva's per-shape perfect-draw
          // double-buffer (no stroke to overlap), a big win at high band counts.
          // Non-listening: hover answers come from tipAt's binary search, so the
          // band never hits the Konva hit canvas and needs no eager _tip string.
          content.add(new Konva.Rect({
            x: px, y: y + 1, width: Math.max(pxEnd - px, 1), height: this.laneH - 2,
            fill: colors[runStart], perfectDrawEnabled: false, listening: false,
          }));
          // ☠️ OMON-295: state lanes used green/red as the SOLE carrier of
          // meaning — the textbook deuteranopia failure (~8% of men), on the
          // highest-stakes lanes we draw ("was the front door open?"). Overlay
          // the 0 state with a diagonal hatch so colour becomes redundant
          // encoding rather than the only encoding. Cheap: state channels emit
          // on change, so there are few runs, and the pattern is one shared
          // 8×8 tile rather than per-band line geometry.
          if (lane.kind === "state" && seg[runStart].value === 0) {
            content.add(new Konva.Rect({
              x: px, y: y + 1, width: Math.max(pxEnd - px, 1), height: this.laneH - 2,
              // Konva types fillPatternImage as HTMLImageElement, but it goes
              // straight to canvas createPattern(), which takes a canvas too —
              // and a canvas avoids a data-URI round-trip through an <img>.
              fillPatternImage: this.hatchTile() as unknown as HTMLImageElement,
              fillPatternRepeat: "repeat",
              perfectDrawEnabled: false, listening: false,
            }));
          }
        }
        runStart = k;
      }
    }
  }
}
